import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import FingerprintJS from '@fingerprintjs/fingerprintjs'; // 🔴 استيراد مكتبة البصمة
import { BookOpen, AlertCircle, PlayCircle, Loader2, Lock } from 'lucide-react'; // 🔴 إضافة Lock للأخطاء
import Footer from './Footer';
import './MySubscriptions.css';

const MySubscriptions = ({ darkMode }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // 🔴 States الجديدة الخاصة بالحماية والأخطاء
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);
  const [errorMessages, setErrorMessages] = useState({});
  
  const navigate = useNavigate();
  const joinClass = (meetingId) => {
    // تحويل المستخدم لصفحة زووم المعزولة
    navigate(`/live-class/${meetingId}`);
};
  // ====== 1. جلب بصمة الجهاز عند فتح الصفحة ======
  useEffect(() => {
    const fetchFingerprint = async () => {
      try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        setDeviceFingerprint(result.visitorId);
        localStorage.setItem('deviceFingerprint', result.visitorId);
      } catch (err) {
        console.error("Fingerprint error", err);
      }
    };
    fetchFingerprint();
  }, []);

  // ====== 2. جلب الاشتراكات ======
  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          navigate('/Login');
          return;
        }

        const response = await axios.get('https://leader-acadmy.hwnix.com/api/user/subscriptions', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data && response.data.status) {
          setPackages(response.data.packages);
        } else {
          setError("لم نتمكن من جلب اشتراكاتك في الوقت الحالي.");
        }
      } catch (err) {
        console.error("Error fetching subscriptions:", err);
        setError("انتهت جلسة تسجيل الدخول الرجاء اعادة تسجيل الدخول مرة اخري");
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
  }, [navigate]);

  // ====== 3. دالة الدخول المحمية ======
  const handleOpenPackage = async (pkg) => {
    localStorage.setItem('package_id', pkg.id);
    const id = localStorage.getItem('id');
    const subscriptionCode = localStorage.getItem('subscriptionCode');
    const currentFingerprint = localStorage.getItem('deviceFingerprint') || deviceFingerprint;

    // تصفير الخطأ لهذا الكارت إن وجد
    setErrorMessages(prev => ({ ...prev, [pkg.id]: null }));

    try {
      // إرسال طلب التحقق من البصمة والكود
      const response = await axios.get(
        `https://leader-acadmy.hwnix.com/api/code/check/${id}/${currentFingerprint}/${pkg.id}?code=${subscriptionCode}`
      );

      // لو الرد سليم، ندخله لصفحة الدروس
      if (response.data.message === 'User has a valid code.') {
        navigate('/ProfileCard'); 
      } else {
        setErrorMessages(prev => ({ ...prev, [pkg.id]: 'الكود غير صالح أو يوجد مشكلة.' }));
      }
    } catch (err) {
      // التقاط إيرور الـ MAC address أو أي إيرور آخر
      const msg = err.response?.data?.message;
      let errorText = 'برجاء التأكد من صلاحية الاشتراك';
      if (msg === 'MAC address mismatch.') {
        errorText = 'برجاء الدخول من الجهاز الأصلي الذي تم التسجيل منه';
      }
      setErrorMessages(prev => ({ ...prev, [pkg.id]: errorText }));
    }
  };

  // دالة لمعرفة اسم المادة
  const getSubjectName = (id) => {
    if (!id) return "مادة غير معروفة";
    const baseSubjects = [
      "اللغة العربية", "اللغة الإنجليزية", "الجغرافيا", "التاريخ", 
      "الرياضيات", "الفيزياء", "العلوم", "الكيمياء", 
      "الحاسب الآلي", "الأحياء", "الإحصاء", "الفلسفة"
    ];
    const index = (parseInt(id) - 1) % 12;
    return baseSubjects[index];
  };

  const gradients = [
    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    "linear-gradient(135deg, #ff0844 0%, #ffb199 100%)",
    "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
    "linear-gradient(135deg, #FF8008 0%, #FFC837 100%)",
    "linear-gradient(135deg, #3B2667 0%, #BC78EC 100%)"
  ];

  return (
    <div className={`subscriptions-page ${darkMode ? 'dark-mode' : ''}`}>
      <div className="subs-container">
        
        <header className="subs-header">
          <motion.div 
            initial={{ opacity: 0, y: -20 }} 
            animate={{ opacity: 1, y: 0 }}
            className="header-content"
          >
            <h1>مرحباً بك في <span>عالمك التعليمي</span></h1>
            <p>هنا تجد جميع الباقات والمواد التي اشتركت بها، ابدأ رحلتك الآن.</p>
          </motion.div>
        </header>

        <main className="subs-main">
          {loading ? (
            <div className="subs-grid">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="skeleton-card">
                  <div className="skeleton-header"></div>
                  <div className="skeleton-body">
                    <div className="skeleton-line title"></div>
                    <div className="skeleton-line desc"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="subs-state-message error">
              <AlertCircle size={50} />
              <h3>عذراً!</h3>
              <p>{error}</p>
              <button onClick={() => window.location.reload()} className="retry-btn">حاول مجدداً</button>
            </div>
          ) : packages.length === 0 ? (
            <div className="subs-state-message empty">
              <BookOpen size={60} className="empty-icon" />
              <h3>لا يوجد لديك اشتراكات حالياً</h3>
              <p>تصفح كورساتنا وانضم إلينا لتبدأ التعلم.</p>
              <button onClick={() => navigate('/PricingCard')} className="browse-courses-btn">تصفح الكورسات</button>
            </div>
          ) : (
            <div className="subs-grid">
              <AnimatePresence>
                {packages.map((pkg, index) => (
                  <motion.div
                    key={pkg.id}
                    className="sub-card"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    whileHover={{ y: -8 }}
                    onClick={() => handleOpenPackage(pkg)} // 🔴 تمرير الـ pkg كامل مش الـ ID بس
                  >
                    <div 
                      className="sub-card-banner"
                      style={{ background: gradients[index % gradients.length] }}
                    >
                      <div className="banner-icon-wrapper">
                        <BookOpen size={32} color="#fff" />
                      </div>
                      <div className="banner-overlay"></div>
                    </div>

                    <div className="sub-card-content">
                      <div className="tags-row">
                        <span className="subject-tag">
                          {getSubjectName(pkg.subject_id)}
                        </span>
                      </div>
                      <h3 className="pkg-title">{pkg.title}</h3>
                      <h3 className="pkg-title">{pkg.teacher_id}</h3>

                      <p className="pkg-desc">{pkg.description || "لا يوجد وصف متاح لهذه الباقة."}</p>
                      
                      {/* 🔴 إظهار رسالة الخطأ (Mismatch) داخل الكارت لو حصلت */}
                      {errorMessages[pkg.id] && (
                        <div style={{ marginTop: '15px', color: '#d63031', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold', background: 'rgba(214, 48, 49, 0.1)', padding: '8px', borderRadius: '8px' }}>
                          <Lock size={14} /> 
                          {errorMessages[pkg.id]}
                        </div>
                      )}
                    </div>

                    <div className="sub-card-footer">
                      <button className="enter-course-btn">
                        <span>متابعة التعلم</span>
                        <PlayCircle size={18} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </main>

      </div>
      {/* <div>
            <h1>جدول الحصص</h1>
            <button onClick={() => joinClass('81054526551')}>
                انضم الآن للحصة
            </button>
        </div> */}
        <Footer/>
    </div>
  );
};

export default MySubscriptions;